import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import {
  CountryISO,
  PhoneNumberFormat,
  SearchCountryField,
} from 'ngx-intl-tel-input-gg';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { passwordMatchValidator } from './confirmed.validator';
import { TranslateService } from '../../services/translate/translate.service';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  tap,
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Route, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { RegisterService } from '../../services/register/register.service';
import { UsersInfo } from '../../interfaces/user.interface';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AddressModalComponent } from '../../components/address-modal/address-modal.component';
import { Address } from '../../interfaces/address.interface';
import { DataLoginService } from '../../services/dataLogin/data-login.service';
import { MangoEventAnalyticsService } from '../../services/mango-events-analytics/mango-events-analytics.service';

type DecriptedToken = {
  email: string;
  iat: number;
  exp: number;
  group: {
    pk: string;
    sk: string;
    status: string;
  };
  username: string;
};
interface ConfigRegister {
  viewRegister: boolean;
  fieldsByUsername: string;
  groupsDefault: {
    pk: string;
    sk: string;
    status: string;
    color: string;
  }[];
  fieldsView: {
    firstName: boolean;
    lastName: boolean;
    avatar: boolean;
    username: boolean;
    email: boolean;
    address: boolean;
    phone: boolean;
    password: boolean;
    confirmPassword: boolean;
    terms: boolean;
    policy: boolean;
  };
}

@Component({
  selector: 'mg-registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss'],
})
export class RegistrationComponent implements OnInit, OnDestroy {
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  separateDialCode = false;
  PhoneNumberFormat = PhoneNumberFormat;
  selectedCountryISO = CountryISO.UnitedStates;
  preferredCountries: CountryISO[] = [
    CountryISO.UnitedStates,
    CountryISO.Colombia,
  ];
  isDragActive = false;
  unsubscribe$ = new Subject();
  fileUrl = '';
  version: string = '';
  tamWidth = 768;
  activeVisibilityPassword = false;
  activeVisibilityConfirmPassword = false;
  formRegister!: FormGroup;
  code: string = '';
  dataTranslateRegister!: any;
  token!: string;
  url: string = '';
  urlTerms: string = '';
  urlPrivacy: string = '';
  stateUsername: boolean = false;
  stateEmail: boolean = false;
  stateAddress: boolean = true;
  searchEmail$ = new Subject<string>();
  searchUsername$ = new Subject<string>();
  configRegister: ConfigRegister = {
    viewRegister: true,
    fieldsByUsername: 'email',
    groupsDefault: [],
    fieldsView: {
      firstName: true,
      lastName: true,
      avatar: false,
      username: false,
      email: true,
      address: true,
      phone: false,
      password: true,
      confirmPassword: true,
      terms: true,
      policy: true,
    },
  };
  stateValidateEmail:
    | 'default'
    | 'active'
    | 'loading'
    | 'pending'
    | 'send'
    | 'invalid'
    | 'valid' = 'default';

  statePhone: boolean = false;
  loading: boolean = false;
  validate: boolean = false;
  loadingValidateUsername: boolean = false;
  config: any;
  params!: any;

  constructor(
    private serviceDataLogin: DataLoginService,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private toast: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private jwt: JwtHelperService,
    private registerService: RegisterService,
    private dialog: MatDialog,
    private mangoEventAnalyticsService: MangoEventAnalyticsService
  ) {
    this.tamWidth = window.innerWidth;
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((data: any) => {
      this.params = data.params;
    });
    this.mangoEventAnalyticsService.sendEvent('register_start', {
      timestamp: new Date().toISOString(),
    });
    this.createForm();
    const config = this.serviceDataLogin.configData;
    if (!config) {
      this.serviceDataLogin.configObservable.subscribe((config) => {
        this.config = config;
        this.setConfigs();
      });
    } else {
      this.config = config;
      this.setConfigs();
    }
    this.validateToken();
    this.searchEmail$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => {
        this.validateEmail(value);
      });
    this.searchUsername$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => {
        this.validateUsername(value);
      });
    this.dataTranslate();
    this.version = (this.serviceDataLogin.login().version as string) || '0.0.0';
    this.url = this.serviceDataLogin.login().url || '';
    this.urlPrivacy = this.serviceDataLogin.login().urlPrivacy || '';
    this.urlTerms = this.serviceDataLogin.login().urlTerms || '';
  }

  setConfigs() {
    if (
      this.config &&
      this.config?.CONFIG_REGISTER &&
      this.config?.CONFIG_REGISTER?.viewRegister
    ) {
      this.configRegister = this.config?.CONFIG_REGISTER;
      const defaultFields: any = {
        firstName: true,
        lastName: true,
        avatar: true,
        username: true,
        email: true,
        address: true,
        phone: true,
        password: true,
        confirmPassword: true,
        terms: true,
        policy: true,
      };
      if (!this.configRegister.fieldsView) {
        this.configRegister['fieldsView'] = defaultFields;
        return;
      }
      Object.keys(defaultFields).forEach((key: any) => {
        if (!(key in this.configRegister['fieldsView'])) {
          this.configRegister['fieldsView'][
            key as keyof (typeof this.configRegister)['fieldsView']
          ] = defaultFields[key];
        }
        if (
          this.formRegister.controls[key] &&
          !this.configRegister['fieldsView'][
            key as keyof (typeof this.configRegister)['fieldsView']
          ]
        ) {
          const control = this.formRegister.get(key);
          if (control) {
            control.clearValidators(); // Elimina las validaciones
            control.updateValueAndValidity(); // Actualiza el estado del control
          }
          switch (key) {
            case 'email':
              this.stateEmail = false;
              this.stateValidateEmail === 'valid';
              break;
            case 'username':
              this.stateUsername = false;
              break;
            case 'phone':
              this.statePhone = false;
              break;
          }
        }
      });
    } else {
      this.back();
    }
  }
  dataTranslate() {
    this.translateService
      .init('core')
      .pipe(
        tap((data) => {
          this.dataTranslateRegister = data.login.register;
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }
  validateToken() {
    this.token = this.route.snapshot.paramMap.get('id') as string;
    if (this.token) {
      const infoToken: DecriptedToken = this.jwt.decodeToken(
        this.token
      ) as DecriptedToken;
      this.formRegister.controls['email'].setValue(infoToken.email);
      this.formRegister.controls['email'].disable({ onlySelf: true });
      this.stateValidateEmail = 'valid';
    }
  }

  openAddressModal() {
    this.mangoEventAnalyticsService.sendEvent('register_address_modal_open', {
      timestamp: new Date().toISOString(),
    });
    const dialogRef = this.dialog.open(AddressModalComponent, {
      height: '666px',
      width: '500px',
      data: this.formRegister.getRawValue().address || {},
    });

    dialogRef.afterClosed().subscribe((address: Address) => {
      if (address) {
        this.formRegister.controls['address'].setValue(address);
        this.mangoEventAnalyticsService.sendEvent('register_address_saved', {
          address: address.address,
          city: address.city,
          country: address.country,
          timestamp: new Date().toISOString(),
        });
      }
    });
  }
  createForm() {
    this.formRegister = this.formBuilder.group(
      {
        firstName: ['', [Validators.required]],
        address: this.formBuilder.group(
          {
            address: [{ value: '', disabled: true }, Validators.required], // Aquí se coloca el valor de la dirección
            references: '',
            lat: '',
            lng: '',
            country: '',
            state: '',
            city: '',
            zipCode: '',
          },
          { validators: this.addressRequiredValidator }
        ),
        lastName: ['', [Validators.required]],
        username: ['', [Validators.required]],
        email: ['', [Validators.required]],
        phone: ['', [Validators.required]],
        password: ['', [Validators.required, Validators.minLength(4)]],
        confirmPassword: ['', [Validators.required, Validators.minLength(4)]],
        terms: [false, [Validators.required, Validators.requiredTrue]],
        policy: [false, [Validators.required, Validators.requiredTrue]],
      },
      { validator: passwordMatchValidator }
    );
  }
  addressRequiredValidator(group: FormGroup): { [key: string]: any } | null {
    const address = group.get('address')?.value;
    const lat = group.get('lat')?.value;
    const lng = group.get('lng')?.value;

    // Aquí puedes personalizar las validaciones que quieres hacer requeridas
    if (!address || !lat || !lng) {
      return { addressRequired: true }; // Si falla la validación
    }
    return null; // Si pasa la validación
  }

  getRegex(length: number): string {
    return `(?=.*[A-Z])(?=.*[!@#$&/*.)(?¡¿!°|'}+{-])(?=.*[0-9])(?=.*[a-z]).{${length},}`;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.tamWidth = event.target.innerWidth;
  }

  urlUploadedHandle({ url }: { url: string }) {
    this.fileUrl = url;
  }

  get f(): any {
    return this.formRegister.controls;
  }

  validateUsername(email: string) {
    this.loadingValidateUsername = true;
    this.mangoEventAnalyticsService.sendEvent('register_username_validation', {
      username: email,
      timestamp: new Date().toISOString(),
    });
    this.registerService.validateUsername(this.url, email).subscribe((resp) => {
      this.stateUsername = resp.elementFind;
      this.loadingValidateUsername = false;
      this.mangoEventAnalyticsService.sendEvent(
        resp.elementFind ? 'register_username_taken' : 'register_username_available',
        {
          username: email,
          timestamp: new Date().toISOString(),
        }
      );
    });
  }

  searchInputUsername(event: any) {
    const username: string = event.target.value;
    this.searchUsername$.next(username);
  }

  searchInputEmail(event: any) {
    const value = event.target.value;
    if (this.stateValidateEmail === 'valid') {
      this.stateValidateEmail = 'default';
    }
    this.searchEmail$.next(value);
  }

  validateEmail(email: string) {
    this.stateValidateEmail = 'loading';
    this.mangoEventAnalyticsService.sendEvent('register_email_validation', {
      email: email,
      timestamp: new Date().toISOString(),
    });
    this.registerService.validateEmail(this.url, email).subscribe((resp) => {
      this.stateEmail = resp.elementFind;
      if (email.length > 0 && !this.stateEmail) {
        this.stateValidateEmail = 'active';
        this.mangoEventAnalyticsService.sendEvent('register_email_available', {
          email: email,
          timestamp: new Date().toISOString(),
        });
      } else {
        this.stateValidateEmail = 'default';
        this.mangoEventAnalyticsService.sendEvent('register_email_taken', {
          email: email,
          timestamp: new Date().toISOString(),
        });
      }
    });
  }
  sendEmailCode() {
    switch (this.stateValidateEmail) {
      case 'active':
        this.code = '';
        this.formRegister.controls['email'].disable({ onlySelf: true });
        this.sendCode();
        break;
      case 'pending':
        this.stateValidateEmail = 'loading';
        const email = this.formRegister.getRawValue().email;
        this.mangoEventAnalyticsService.sendEvent('register_email_code_verify', {
          email: email,
          timestamp: new Date().toISOString(),
        });
        this.registerService
          .validateEmailOtp(this.url, email, this.code)
          .subscribe({
            next: () => {
              this.stateValidateEmail = 'valid';
              this.formRegister.controls['email'].enable();
              this.mangoEventAnalyticsService.sendEvent('register_email_code_verified', {
                email: email,
                timestamp: new Date().toISOString(),
              });
            },
            error: () => {
              this.stateValidateEmail = 'invalid';
              this.formRegister.controls['email'].enable();
              this.mangoEventAnalyticsService.sendEvent('register_email_code_invalid', {
                email: email,
                timestamp: new Date().toISOString(),
              });
            },
          });
        break;
      case 'invalid':
        this.stateValidateEmail = 'loading';
        const emailRetry = this.formRegister.getRawValue().email;
        this.mangoEventAnalyticsService.sendEvent('register_email_code_verify_retry', {
          email: emailRetry,
          timestamp: new Date().toISOString(),
        });
        this.registerService
          .validateEmailOtp(this.url, emailRetry, this.code)
          .subscribe({
            next: () => {
              this.stateValidateEmail = 'valid';
              this.formRegister.controls['email'].enable();
              this.mangoEventAnalyticsService.sendEvent('register_email_code_verified', {
                email: emailRetry,
                timestamp: new Date().toISOString(),
              });
            },
            error: () => {
              this.stateValidateEmail = 'invalid';
              this.formRegister.controls['email'].enable();
              this.mangoEventAnalyticsService.sendEvent('register_email_code_invalid', {
                email: emailRetry,
                timestamp: new Date().toISOString(),
              });
            },
          });
        break;
    }
  }

  onCodeCompleted(code: any) {
    this.code = code.target.value;
  }
  redirectTerms() {
    this.mangoEventAnalyticsService.sendEvent('register_terms_click', {
      timestamp: new Date().toISOString(),
    });
    window.open(this.urlTerms, '_target');
  }

  redirectPrivacy() {
    this.mangoEventAnalyticsService.sendEvent('register_privacy_click', {
      timestamp: new Date().toISOString(),
    });
    window.open(this.urlPrivacy, '_target');
  }
  sendCode() {
    this.stateValidateEmail = 'loading';
    const email = this.formRegister.getRawValue().email;
    this.mangoEventAnalyticsService.sendEvent('register_email_code_sent', {
      email: email,
      timestamp: new Date().toISOString(),
    });
    this.registerService
      .sendEmailOtp(this.url, email)
      .subscribe((resp) => {
        this.stateValidateEmail = 'pending';
      });
  }

  validatePhone(e: any) {
    let phone: string = this.formRegister.value.phone
      ? this.formRegister.value.phone.e164Number
      : '+1';
    if (phone.length > 7) {
      this.mangoEventAnalyticsService.sendEvent('register_phone_validation', {
        phone: phone,
        timestamp: new Date().toISOString(),
      });
      this.registerService.validatePhone(this.url, phone).subscribe((resp) => {
        this.statePhone = resp.elementFind;
        this.mangoEventAnalyticsService.sendEvent(
          resp.elementFind ? 'register_phone_taken' : 'register_phone_available',
          {
            phone: phone,
            timestamp: new Date().toISOString(),
          }
        );
      });
    }
  }
  save() {
    if (!this.loading) {
      this.loading = true;
      this.mangoEventAnalyticsService.sendEvent('register_submit', {
        timestamp: new Date().toISOString(),
      });
      if (
        this.formRegister.valid &&
        !this.stateEmail &&
        !this.statePhone &&
        !this.stateUsername
      ) {
        const {
          confirmPassword,
          firstName,
          username,
          lastName,
          phone,
          email,
          password,
          ...infoForm
        } = this.formRegister.value;
        if (!this.token) {
          let user: UsersInfo = {
            fullName: `${firstName.trim()} ${lastName.trim()}`,
            email,
            groups: this.configRegister?.groupsDefault || [],
            modifyGroups: true,
            modifyStatus: false,
            password,
            phone: phone && phone.length > 0 ? phone.e164Number : '',
            picture: this.fileUrl,
            username:
              this.configRegister.fieldsByUsername !== 'username' &&
              this.formRegister.value[this.configRegister.fieldsByUsername]
                ? this.formRegister.value[this.configRegister.fieldsByUsername]
                : username.trim(),
            profile: {
              ...infoForm,
            },
          } as UsersInfo;
          this.registerService.register(this.url, user).subscribe({
            next: async () => {
              this.loading = false;
              this.mangoEventAnalyticsService.sendEvent('register_success', {
                email: user.email,
                username: user.username,
                timestamp: new Date().toISOString(),
              });
              this.toast.success(
                this.dataTranslateRegister.form.toastTitle,
                this.dataTranslateRegister.form.totastContent
              );
              setTimeout(() => {
                this.back();
                this.loading = false;
              }, 2000);
            },
            error: (error) => {
              this.toast.error(error.error.errorMessage, 'Error');
              this.mangoEventAnalyticsService.sendEvent('register_error', {
                error: error.error?.errorMessage || 'Unknown error',
                email: user.email,
                timestamp: new Date().toISOString(),
              });
              console.error(error.error.errorMessage);
              this.loading = false;
            },
          });
        } else {
          const infoToken: DecriptedToken = this.jwt.decodeToken(
            this.token
          ) as DecriptedToken;
          const user: UsersInfo = {
            phone: phone?.e164Number || '',
            username: username.trim(),
            fullName: `${firstName.trim()} ${lastName.trim()}`,
            picture: this.fileUrl,
            email: infoToken.email,
            password,
            groups: [
              {
                pk: infoToken.group.pk,
                sk: infoToken.group.sk,
                status: infoToken.group.status,
              },
            ],
          };
          this.registerService
            .completeRegister(this.url, user, this.token)
            .subscribe({
              next: async ({ data }) => {
                this.loading = false;
                this.mangoEventAnalyticsService.sendEvent('register_complete_success', {
                  email: user.email,
                  username: user.username,
                  timestamp: new Date().toISOString(),
                });
                this.toast.success(
                  this.dataTranslateRegister.form.toastTitle,
                  this.dataTranslateRegister.form.totastContent
                );
                setTimeout(() => {
                  this.back();
                  this.loading = false;
                }, 2000);
              },
              error: (error) => {
                this.toast.error(error.error.errorMessage, 'Error');
                this.mangoEventAnalyticsService.sendEvent('register_complete_error', {
                  error: error.error?.errorMessage || 'Unknown error',
                  email: user.email,
                  timestamp: new Date().toISOString(),
                });
                console.error(error.error.errorMessage);
                this.loading = false;
              },
            });
        }
      }
    }
  }
  back() {
    this.mangoEventAnalyticsService.sendEvent('register_back', {
      timestamp: new Date().toISOString(),
    });
    let queryParams: any = {};
    if (this.params) {
      queryParams = this.params;
    }
    this.router.navigate(['/login'], {
      queryParams,
    });
  }

  urlUploadedErrorHandle(error: string) {
    this.toast.error(error, 'Error');
    console.error(error);
  }

  public onDragOver() {
    this.isDragActive = true;
  }

  public onDragLeave(event: Event) {
    this.isDragActive = false;
  }

  ngOnDestroy(): void {
    this.unsubscribe$?.next(null);
    this.unsubscribe$?.complete();
    this.searchEmail$.next('');
    this.searchUsername$.next('');
    this.searchEmail$.complete();
    this.searchUsername$.complete();
  }
}
